import React from 'react';
import { Building2 } from 'lucide-react';
import { createCheckoutSession, type ServiceType } from '../services/serviceOrderService';
import { HeroSection } from './checkout/HeroSection';
import { ProcessSteps } from './checkout/ProcessSteps';
import { WhatYouGet } from './checkout/WhatYouGet';
import { ComparisonTable } from './checkout/ComparisonTable';
import { StatsBar } from './checkout/StatsBar';
import { ComplianceIssues } from './checkout/ComplianceIssues';
import { ClauseExamples } from './checkout/ClauseExamples';
import { CaseStudies } from './checkout/CaseStudies';
import { Testimonial } from './checkout/Testimonial';
import { FaqSection } from './checkout/FaqSection';
import { FinalCta } from './checkout/FinalCta';
import { CheckoutSidebar } from './checkout/CheckoutSidebar';
import { useScrollReveal } from './checkout/useScrollReveal';
import { CommercialHeroSection } from './checkout/CommercialHeroSection';
import { CommercialProcessSteps } from './checkout/CommercialProcessSteps';
import { CommercialWhatYouGet } from './checkout/CommercialWhatYouGet';
import { CommercialComparisonTable } from './checkout/CommercialComparisonTable';
import { CommercialStatsBar } from './checkout/CommercialStatsBar';
import { CommercialComplianceIssues } from './checkout/CommercialComplianceIssues';
import { CommercialClauseExamples } from './checkout/CommercialClauseExamples';
import { CommercialCaseStudies } from './checkout/CommercialCaseStudies';
import { CommercialTestimonial } from './checkout/CommercialTestimonial';
import { CommercialFaqSection } from './checkout/CommercialFaqSection';
import { CommercialFinalCta } from './checkout/CommercialFinalCta';

interface ServiceCheckoutPageProps {
  serviceType: ServiceType;
  onBack: () => void;
  onPaymentSuccess: (orderId: string) => void;
}

export function ServiceCheckoutPage({ serviceType, onBack, onPaymentSuccess }: ServiceCheckoutPageProps) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const isTenancy = serviceType === 'tenancy_review_rewrite';

  useScrollReveal();

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('order_id');
    const sessionId = params.get('session_id');
    if (orderId && sessionId) {
      window.history.replaceState({}, '', window.location.pathname);
      localStorage.removeItem('pending_service_order');
      onPaymentSuccess(orderId);
    }
  }, [onPaymentSuccess]);

  const handleCheckout = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const base = window.location.origin + window.location.pathname;
      const returnParams = new URLSearchParams(window.location.search);
      returnParams.delete('order_id');
      returnParams.delete('session_id');
      if (!returnParams.has('service')) {
        returnParams.set('service', serviceType);
      }
      const currentUrl = `${base}?${returnParams.toString()}`;
      const result = await createCheckoutSession(serviceType, currentUrl, currentUrl);
      localStorage.setItem('pending_service_order', JSON.stringify({
        orderId: result.orderId,
        serviceType,
        timestamp: Date.now(),
      }));
      window.location.href = result.checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setIsLoading(false);
    }
  };

  const scrollToCheckout = () => {
    const el = document.getElementById('checkout-sidebar');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      handleCheckout();
    }
  };

  if (isTenancy) {
    return (
      <div className="min-h-screen bg-white">
        <HeroSection onCtaClick={scrollToCheckout} />

        <div className="relative">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8 max-w-7xl mx-auto">
            <div className="lg:col-span-8">
              <ProcessSteps />
              <WhatYouGet />
              <ComparisonTable />
            </div>
            <div className="hidden lg:block lg:col-span-4 relative">
              <div className="sticky top-8 pt-20" id="checkout-sidebar">
                <CheckoutSidebar
                  price="15.50"
                  title="Tenancy Agreement Review & Re-Write"
                  isLoading={isLoading}
                  error={error}
                  onCheckout={handleCheckout}
                />
              </div>
            </div>
          </div>

          <StatsBar />

          <div className="max-w-7xl mx-auto">
            <ComplianceIssues />
            <ClauseExamples />
            <CaseStudies />
            <Testimonial />
            <FaqSection />
          </div>

          <FinalCta onCheckout={handleCheckout} isLoading={isLoading} />
        </div>

        <div className="lg:hidden">
          <CheckoutSidebar
            price="15.50"
            title="Tenancy Agreement Review & Re-Write"
            isLoading={isLoading}
            error={error}
            onCheckout={handleCheckout}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <CommercialHeroSection onCtaClick={scrollToCheckout} />

      <div className="relative">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8 max-w-7xl mx-auto">
          <div className="lg:col-span-8">
            <CommercialProcessSteps />
            <CommercialWhatYouGet />
            <CommercialComparisonTable />
          </div>
          <div className="hidden lg:block lg:col-span-4 relative">
            <div className="sticky top-8 pt-20" id="checkout-sidebar">
              <CheckoutSidebar
                price="18.75"
                title="Commercial Lease Review & Re-Write"
                isLoading={isLoading}
                error={error}
                onCheckout={handleCheckout}
                icon={Building2}
              />
            </div>
          </div>
        </div>

        <CommercialStatsBar />

        <div className="max-w-7xl mx-auto">
          <CommercialComplianceIssues />
          <CommercialClauseExamples />
          <CommercialCaseStudies />
          <CommercialTestimonial />
          <CommercialFaqSection />
        </div>

        <CommercialFinalCta onCheckout={handleCheckout} isLoading={isLoading} />
      </div>

      <div className="lg:hidden">
        <CheckoutSidebar
          price="18.75"
          title="Commercial Lease Review & Re-Write"
          isLoading={isLoading}
          error={error}
          onCheckout={handleCheckout}
          icon={Building2}
        />
      </div>
    </div>
  );
}
